import React, { useState, useEffect } from 'react'
import api from '../../api/axios'
import styles from './PartnershipForm.module.css'
import { T, useContent } from '../../content/ContentProvider'

// Real enum values — these match com.hitrail.ngrs.partnership.model.PartnershipType /
// PartnershipStatus. Previously the form hard-coded made-up values
// (COMMISSION/SETTLEMENT_VALUE/INACTIVE/SUSPENDED) which the backend rejected
// with a 500 because Jackson couldn't deserialize them to the enum.
const partnershipTypes = (t) => [
  { value: 'BILATERAL',          label: t('partnerships.form.type.bilateral') },
  { value: 'MULTILATERAL',       label: t('partnerships.form.type.multilateral') },
  { value: 'REVENUE_SHARING',    label: t('partnerships.form.type.revenueSharing') },
  { value: 'SERVICES_AGREEMENT', label: t('partnerships.form.type.servicesAgreement') },
  { value: 'DATA_SHARING',       label: t('partnerships.form.type.dataSharing') },
]

const partnershipStatuses = (t) => [
  { value: 'PENDING',    label: t('partnerships.form.status.pending') },
  { value: 'ACTIVE',     label: t('partnerships.form.status.active') },
  { value: 'TERMINATED', label: t('partnerships.form.status.terminated') },
]

/**
 * Build initial form state from an existing entity (edit mode) or empty defaults
 * (create mode). The entity uses {@code partnershipType} + {@code members:[{company:{id}}]};
 * the form mirrors those names exactly so save/load round-trips cleanly.
 */
function initialFromEntity(p) {
  if (!p) {
    return {
      agreementId: '',
      name: '',
      partnershipType: 'BILATERAL',
      status: 'ACTIVE',
      description: '',
      startDate: '',
      endDate: '',
      memberIds: [],
    }
  }
  // Member ids may live under different paths depending on the API shape:
  //  - admin endpoint returns `members: [{company: {id, rics, name}, memberRole}]`
  //  - flat /api/partnerships endpoint returns `members: [{companyId, rics, name, role}]`
  // Accept both.
  const memberIds = Array.isArray(p.members)
    ? p.members
        .map((m) => (m.company && m.company.id) || m.companyId || null)
        .filter((id) => id != null)
    : []
  return {
    agreementId:     p.agreementId || '',
    name:            p.name || '',
    partnershipType: p.partnershipType || p.type || 'BILATERAL',
    status:          p.status || 'ACTIVE',
    description:     p.description || '',
    startDate:       p.startDate || '',
    endDate:         p.endDate || '',
    memberIds,
  }
}

export function PartnershipForm({ partnership, onSubmit, isLoading }) {
  const { t } = useContent()
  const PARTNERSHIP_TYPES = partnershipTypes(t)
  const PARTNERSHIP_STATUSES = partnershipStatuses(t)
  const [formData, setFormData] = useState(() => initialFromEntity(partnership))
  const [companies, setCompanies] = useState([])
  const [memberFilter, setMemberFilter] = useState('')

  useEffect(() => {
    fetchCompanies()
  }, [])

  useEffect(() => {
    setFormData(initialFromEntity(partnership))
  }, [partnership])

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/api/admin/companies?size=100')
      const data = response.data
      setCompanies(Array.isArray(data) ? data : data.content || [])
    } catch (err) {
      console.error('Failed to fetch companies:', err)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleMemberToggle = (companyId) => {
    setFormData((prev) => ({
      ...prev,
      memberIds: prev.memberIds.includes(companyId)
        ? prev.memberIds.filter((id) => id !== companyId)
        : [...prev.memberIds, companyId],
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.field}>
        <label>{t('partnerships.form.agreementId.label')}</label>
        <input
          type="text"
          name="agreementId"
          value={formData.agreementId}
          onChange={handleChange}
          required
          disabled={isLoading}
          placeholder={t('partnerships.form.agreementId.placeholder')}
        />
      </div>

      <div className={styles.field}>
        <label>{t('partnerships.form.name.label')}</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          disabled={isLoading}
          placeholder={t('partnerships.form.name.placeholder')}
        />
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label>{t('partnerships.form.type.label')}</label>
          <select
            name="partnershipType"
            value={formData.partnershipType}
            onChange={handleChange}
            disabled={isLoading}
          >
            {PARTNERSHIP_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label>{t('partnerships.form.status.label')}</label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            disabled={isLoading}
          >
            {PARTNERSHIP_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label>{t('partnerships.form.startDate.label')}</label>
          <input
            type="date"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            disabled={isLoading}
          />
        </div>
        <div className={styles.field}>
          <label>{t('partnerships.form.endDate.label')}</label>
          <input
            type="date"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            disabled={isLoading}
          />
        </div>
      </div>

      <div className={styles.field}>
        <label>{t('partnerships.form.description.label')}</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          disabled={isLoading}
          rows={2}
          placeholder={t('partnerships.form.description.placeholder')}
        />
      </div>

      <div className={styles.field}>
        <label>
          <T k="partnerships.form.members.label" />
          <span className={styles.memberCounter}>
            <T k="partnerships.form.members.counter" vars={{ selected: formData.memberIds.length, total: companies.length }} />
          </span>
        </label>

        <div className={styles.memberSearchBar}>
          <input
            type="text"
            placeholder={t('partnerships.form.members.searchPlaceholder')}
            value={memberFilter}
            onChange={(e) => setMemberFilter(e.target.value)}
            className={styles.memberSearchInput}
            disabled={isLoading}
          />
          {formData.memberIds.length > 0 && (
            <button
              type="button"
              className={styles.memberClearBtn}
              onClick={() => setFormData((prev) => ({ ...prev, memberIds: [] }))}
              disabled={isLoading}
            >
              {t('partnerships.form.members.clearAll')}
            </button>
          )}
        </div>

        <div className={styles.membersList}>
          {(() => {
            const q = memberFilter.trim().toLowerCase()
            const filtered = q
              ? companies.filter(
                  (c) =>
                    (c.name || '').toLowerCase().includes(q) ||
                    (c.rics || '').toLowerCase().includes(q) ||
                    (c.country || '').toLowerCase().includes(q) ||
                    (c.companyId || '').toLowerCase().includes(q)
                )
              : companies
            const sorted = [...filtered].sort((a, b) => {
              const aSel = formData.memberIds.includes(a.id) ? 0 : 1
              const bSel = formData.memberIds.includes(b.id) ? 0 : 1
              if (aSel !== bSel) return aSel - bSel
              return (a.name || '').localeCompare(b.name || '')
            })
            if (sorted.length === 0) {
              return (
                <div className={styles.membersEmpty}>
                  <T k="partnerships.form.members.noMatch" vars={{ query: memberFilter }} />
                </div>
              )
            }
            return sorted.map((company) => (
              <label key={company.id} className={styles.memberCheckbox}>
                <input
                  type="checkbox"
                  checked={formData.memberIds.includes(company.id)}
                  onChange={() => handleMemberToggle(company.id)}
                  disabled={isLoading}
                />
                <span className={styles.memberName}>{company.name}</span>
                {company.rics && <span className={styles.memberRics}>{company.rics}</span>}
                {company.country && <span className={styles.memberCountry}>{company.country}</span>}
              </label>
            ))
          })()}
        </div>
      </div>

      <div className={styles.actions}>
        <button type="submit" disabled={isLoading} className={styles.submitBtn}>
          {isLoading ? t('partnerships.form.saving') : t('partnerships.form.submit')}
        </button>
      </div>
    </form>
  )
}
